import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { MatPaginator, MatPaginatorIntl, PageEvent } from "@angular/material/paginator";
import { Subscription, Subject, from, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from "rxjs/operators";
import {
  CriticalPointTemplateSearchParams,
  CriticalPointTemplateSearchResponse,
  CriticalPointTemplateService,
} from "../../services/critical-point-template.service";
import { CriticalPointTemplate } from "src/app/classes/model/practice-path";

const SORT_OPTION_STORAGE_KEY = "criticalPointTemplate_sortOption";

@Component({
  selector: "app-critical-point-template-selector-dialog",
  templateUrl: "./critical-point-template-selector-dialog.component.html",
  styleUrls: ["./critical-point-template-selector-dialog.component.scss"],
})
export class CriticalPointTemplateSelectorDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  protected readonly pageSizeOptions: number[] = [10, 25, 50];
  protected filterForm: FormGroup;
  protected sortForm: FormGroup;
  protected templates: CriticalPointTemplate[] = [];
  protected totalCount = 0;
  protected isLoading = false;
  protected hasError = false;

  // Expose current filter values for highlighting
  protected get titleFilter(): string {
    return this.filterForm.get("title")?.value?.trim() ?? "";
  }

  protected get descriptionFilter(): string {
    return this.filterForm.get("description")?.value?.trim() ?? "";
  }

  protected readonly sortOptions = [
    { value: "createdAt-desc", label: "Létrehozás dátuma (újabb elől)" },
    { value: "createdAt-asc", label: "Létrehozás dátuma (régebbi elől)" },
    { value: "usageCount-desc", label: "Használat száma (több elől)" },
    { value: "usageCount-asc", label: "Használat száma (kevesebb elől)" },
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly loadTemplatesSubject$ = new Subject<void>();
  private loadTemplatesSubscription: Subscription;
  private currentPage = 1;
  protected pageSize = this.pageSizeOptions[0];

  constructor(
    private readonly dialogRef: MatDialogRef<CriticalPointTemplateSelectorDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly templateService: CriticalPointTemplateService,
    private readonly matPaginatorIntl: MatPaginatorIntl
  ) {
    this.filterForm = this.formBuilder.group({
      title: [""],
      description: [""],
    });

    // Load the last selected sort option from localStorage
    const savedSortOption = this.getSavedSortOption();
    this.sortForm = this.formBuilder.group({
      sortOption: [savedSortOption],
    });
  }

  ngOnInit(): void {
    this.matPaginatorIntl.itemsPerPageLabel = "";

    this.filterForm.valueChanges
      .pipe(
        debounceTime(400),
        map(() => this.getNormalizedFilters()),
        distinctUntilChanged(
          (prev, curr) =>
            prev.titleContains === curr.titleContains &&
            prev.descriptionContains === curr.descriptionContains
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.resetToFirstPage();
        this.triggerLoadTemplates();
      });

    this.sortForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.saveSortOption(value.sortOption);
        this.resetToFirstPage();
        this.triggerLoadTemplates();
      });

    this.loadTemplatesSubscription = this.loadTemplatesSubject$
      .pipe(
        switchMap(() => {
          this.isLoading = true;
          this.hasError = false;
          const params = this.buildSearchParams();
          return from(this.templateService.searchTemplates(params)).pipe(
            catchError(() => {
              return of(undefined);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: CriticalPointTemplateSearchResponse | undefined) => {
        this.isLoading = false;
        if (!response) {
          this.handleErrorState();
          return;
        }
        this.handleSuccessState(response);
      });

    this.triggerLoadTemplates();
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator.pageSize = this.pageSize;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadTemplatesSubscription?.unsubscribe();
  }

  protected onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.triggerLoadTemplates();
  }

  protected clearFilter(field: "title" | "description"): void {
    if (!this.filterForm.get(field)) {
      return;
    }
    if (!this.filterForm.get(field)?.value) {
      return;
    }
    this.filterForm.patchValue({ [field]: "" });
  }

  protected async onUseTemplate(template: CriticalPointTemplate): Promise<void> {
    // Increment usage count
    await this.templateService.incrementUsageCount(template.id);

    // Close dialog with the selected template
    this.dialogRef.close(template);
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  private triggerLoadTemplates(): void {
    this.loadTemplatesSubject$.next();
  }

  private resetToFirstPage(): void {
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  private getNormalizedFilters(): Partial<CriticalPointTemplateSearchParams> {
    const title = this.filterForm.get("title")?.value?.trim() ?? "";
    const description = this.filterForm.get("description")?.value?.trim() ?? "";

    return {
      titleContains: title.length > 0 ? title : undefined,
      descriptionContains: description.length > 0 ? description : undefined,
    };
  }

  private buildSearchParams(): CriticalPointTemplateSearchParams {
    const normalizedFilters = this.getNormalizedFilters();
    const sortOption = this.sortForm.get("sortOption")?.value ?? "createdAt-desc";
    const [sortBy, sortOrder] = sortOption.split("-") as [
      "createdAt" | "usageCount",
      "asc" | "desc"
    ];

    return {
      page: this.currentPage,
      pageSize: this.pageSize,
      sortBy,
      sortOrder,
      ...normalizedFilters,
    };
  }

  private handleErrorState(): void {
    this.hasError = true;
    this.templates = [];
    this.totalCount = 0;
    if (this.paginator) {
      this.paginator.length = 0;
      this.paginator.pageIndex = 0;
    }
  }

  private handleSuccessState(response: CriticalPointTemplateSearchResponse): void {
    this.hasError = false;
    this.templates = response.items ?? [];
    this.totalCount = response.totalCount ?? 0;
    this.currentPage = response.page ?? 1;
    this.pageSize = response.pageSize ?? this.pageSize;

    if (this.paginator) {
      this.paginator.length = this.totalCount;
      this.paginator.pageIndex = Math.max(this.currentPage - 1, 0);
      this.paginator.pageSize = this.pageSize;
    }
  }

  private getSavedSortOption(): string {
    try {
      const saved = localStorage.getItem(SORT_OPTION_STORAGE_KEY);
      if (saved && this.sortOptions.some(opt => opt.value === saved)) {
        return saved;
      }
    } catch (error) {
      console.error("Error loading saved sort option:", error);
    }
    return "usageCount-desc"; // default: most used first
  }

  private saveSortOption(sortOption: string): void {
    try {
      localStorage.setItem(SORT_OPTION_STORAGE_KEY, sortOption);
    } catch (error) {
      console.error("Error saving sort option:", error);
    }
  }
}

